import React, { useEffect, useRef, useState } from 'react';
import { Navigation, Clock, MapPin, Euro, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { loadGoogleMaps, HAS_GOOGLE_MAPS } from '../lib/googleMaps';
import { formatDuration } from '../lib/formatDuration';
import { MAX_ACTIVE_BIDS, isBiddingClosedFor } from '../config/bidding';

/**
 * Driver Map View — plots every available job's drop-off location on a Google
 * Map. Customer/driver names + contact details are intentionally HIDDEN before
 * a bid is accepted. Clicking a pin opens an info window showing pickup,
 * drop-off, distance, duration and a "Submit offer" button which delegates
 * back to the existing bid flow via `onBidJob(job)`.
 *
 * Props:
 *   jobs:        list of available delivery objects (must have dropoff_lat/lng)
 *   onBidJob:    (job) => void  — opens the existing bid Dialog
 *   height:      CSS height for the map container
 *   canBid:      boolean — false hides the bid button (e.g. driver hasn't
 *                completed Stripe Connect)
 *   stripeReadyMessage: string shown when canBid is false
 */
export const JobsMapView = ({
  jobs,
  onBidJob,
  height = '500px',
  canBid = true,
  stripeReadyMessage = 'Setup Stripe to bid',
  isPremium = false,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  // Initial map load.
  useEffect(() => {
    let alive = true;
    if (!HAS_GOOGLE_MAPS || !containerRef.current) return undefined;
    loadGoogleMaps()
      .then(() => {
        if (!alive) return;
        // eslint-disable-next-line no-undef
        const g = google;
        if (!mapRef.current) {
          mapRef.current = new g.maps.Map(containerRef.current, {
            center: { lat: 53.4, lng: -7.9 }, // centre of Ireland
            zoom: 7,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: 'greedy',
          });
          infoWindowRef.current = new g.maps.InfoWindow();
        }
        setReady(true);
      })
      .catch(() => setError('Map failed to load'));
    return () => { alive = false; };
  }, []);

  // Re-plot markers when jobs change.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line no-undef
    const g = google;

    // Clear old markers
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const plottable = (jobs || []).filter(
      (j) => j.dropoff_lat != null && j.dropoff_lng != null
    );
    if (plottable.length === 0) return;

    const bounds = new g.maps.LatLngBounds();

    plottable.forEach((job) => {
      const pos = { lat: job.dropoff_lat, lng: job.dropoff_lng };
      const marker = new g.maps.Marker({
        position: pos,
        map: mapRef.current,
        icon: makeJobMarkerIcon(),
        title: `${job.pickup_county || 'Pickup'} → ${job.dropoff_county || 'Drop-off'}`,
      });
      marker.addListener('click', () => {
        const html = renderInfoWindowHTML(job, canBid, stripeReadyMessage, isPremium);
        infoWindowRef.current.setContent(html);
        infoWindowRef.current.open({ map: mapRef.current, anchor: marker });

        // Wire the "Bid" button click after the info window content renders.
        setTimeout(() => {
          const btn = document.getElementById(`jd-mapbid-btn-${job.id}`);
          if (btn) {
            btn.onclick = () => {
              infoWindowRef.current.close();
              onBidJob?.(job);
            };
          }
        }, 50);
      });
      markersRef.current.push(marker);
      bounds.extend(pos);
    });

    if (plottable.length === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(11);
    } else {
      mapRef.current.fitBounds(bounds, 80);
    }
  }, [jobs, ready, canBid, stripeReadyMessage, isPremium, onBidJob]);

  if (!HAS_GOOGLE_MAPS) {
    return (
      <div className="w-full bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
        Map view disabled — Google Maps key not configured.
      </div>
    );
  }

  const noPlottable = (jobs || []).every(
    (j) => j.dropoff_lat == null || j.dropoff_lng == null
  );

  return (
    <div className="w-full" data-testid="driver-jobs-map">
      <div
        ref={containerRef}
        className="w-full rounded-md overflow-hidden border border-gray-200 bg-gray-50"
        style={{ height }}
        data-testid="driver-jobs-map-canvas"
      />
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {!error && noPlottable && (jobs || []).length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          None of the {jobs.length} available {jobs.length === 1 ? 'job has' : 'jobs have'} map coordinates yet — switch to the list view to bid on these.
        </p>
      )}
      {!error && (jobs || []).length === 0 && (
        <p className="mt-2 text-xs text-gray-500">No available jobs match your filters.</p>
      )}
    </div>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────

function makeJobMarkerIcon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0z" fill="#2E7D32"/>
      <circle cx="16" cy="16" r="9" fill="white"/>
      <path d="M21 12h-3l-1-1h-2l-1 1h-3v8h10v-8z M16 18a2 2 0 100-4 2 2 0 000 4z" fill="#2E7D32" transform="translate(-1, -1)"/>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    // eslint-disable-next-line no-undef
    scaledSize: new google.maps.Size(32, 40),
    // eslint-disable-next-line no-undef
    anchor: new google.maps.Point(16, 40),
  };
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Build the InfoWindow HTML. Customer NAMES and CONTACT details are
 * intentionally OMITTED — drivers only see route + item details + distance
 * before bidding.
 */
function renderInfoWindowHTML(job, canBid, stripeReadyMessage, isPremiumDriver = false) {
  const pickup = escapeHtml(job.pickup_location || '—');
  const dropoff = escapeHtml(job.dropoff_location || '—');
  const itemDesc = escapeHtml(job.item_description || '');
  const itemType = escapeHtml(job.item_type || '');
  const dist = job.distance_km != null ? `${job.distance_km} km` : null;
  const dur = job.duration_min != null ? formatDuration(job.duration_min) : null;
  const weight = job.weight_kg ? `${job.weight_kg} kg` : null;
  const dims = (job.dimensions_length || job.dimensions_width || job.dimensions_height)
    ? `${[job.dimensions_length, job.dimensions_width, job.dimensions_height].map(v => v ? `${v}` : '?').join('×')}cm`
    : null;
  const mdrop = Array.isArray(job.additional_drops) && job.additional_drops.length > 0
    ? `<span style="display:inline-block;background:#dbeafe;color:#1d4ed8;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;margin-left:4px;">Multi-drop · ${job.additional_drops.length + 1} stops</span>`
    : '';
  const meta = [dist, dur, weight, dims, itemType].filter(Boolean).map(escapeHtml).join(' · ');
  const timeBadge = renderTimeBadge(job.preferred_time);

  // Help warnings — only show the things that affect a driver's bid.
  const helpRows = [];
  if (job.driver_assistance === 'helper_required') {
    helpRows.push(`<div style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:#fef3c7;color:#78350f;border:1px solid #fde68a;border-radius:999px;font-size:11px;font-weight:700;margin:1px 0;">👥 Helper required</div>`);
  }
  if (job.collection_help === 'none' || job.delivery_help === 'none') {
    const labels = [];
    if (job.collection_help === 'none') labels.push('No load help');
    if (job.delivery_help === 'none') labels.push('No unload help');
    helpRows.push(`<div style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:#fee2e2;color:#991b1b;border:1px solid #fecaca;border-radius:999px;font-size:11px;font-weight:600;margin:1px 0;">❌ ${escapeHtml(labels.join(' · '))}</div>`);
  }
  const helpHtml = helpRows.length > 0 ? `<div style="margin:6px 0 4px;display:flex;flex-wrap:wrap;gap:4px;">${helpRows.join('')}</div>` : '';

  // Active bid counter — urgency without revealing competitor amounts
  const activeBidCount = Math.min(job.active_bid_count || 0, MAX_ACTIVE_BIDS);
  const biddingFull = isBiddingClosedFor(job.active_bid_count, isPremiumDriver);
  const counterHtml = biddingFull
    ? `<div style="margin:6px 0;padding:4px 8px;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:4px;font-size:11px;color:#4b5563;font-weight:600;">✅ Bidding closed — awaiting customer decision.</div>`
    : `<div style="margin:6px 0;padding:4px 8px;background:#eff6ff;border:1px solid #dbeafe;border-radius:4px;font-size:11px;color:#1e40af;font-weight:600;">${activeBidCount}/${MAX_ACTIVE_BIDS} driver bids submitted</div>`;

  const bidBtn = biddingFull
    ? `<div style="margin-top:8px;background:#f3f4f6;color:#6b7280;padding:8px 12px;border-radius:4px;font-size:13px;text-align:center;font-weight:600;">Bidding closed</div>`
    : canBid
      ? `<button id="jd-mapbid-btn-${escapeHtml(job.id)}" style="margin-top:8px;background:#2E7D32;color:#fff;border:0;padding:8px 12px;border-radius:4px;font-size:13px;font-weight:600;cursor:pointer;width:100%;">Submit offer</button>`
      : `<div style="margin-top:8px;background:#fef3c7;color:#92400e;padding:6px 10px;border-radius:4px;font-size:12px;text-align:center;">${escapeHtml(stripeReadyMessage)}</div>`;

  return `
    <div style="font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;max-width:260px;color:#111827;">
      <div style="font-weight:700;font-size:13px;margin-bottom:6px;">
        ${escapeHtml(job.pickup_county || 'Pickup')} → ${escapeHtml(job.dropoff_county || 'Drop-off')}${mdrop}
      </div>
      ${timeBadge}
      ${helpHtml}
      <div style="font-size:12px;color:#374151;line-height:1.5;margin:6px 0 4px;">
        <div><span style="color:#9ca3af;">Pickup:</span> ${pickup}</div>
        <div><span style="color:#9ca3af;">Drop-off:</span> ${dropoff}</div>
      </div>
      ${itemDesc ? `<div style="font-size:12px;color:#6b7280;font-style:italic;margin:4px 0;">${itemDesc}</div>` : ''}
      ${meta ? `<div style="font-size:11px;color:#1B5E20;font-weight:600;margin-top:4px;">${meta}</div>` : ''}
      ${counterHtml}
      ${bidBtn}
    </div>
  `;
}

// Mini inline version of DeliveryTimeBadge for Google InfoWindow HTML.
// Keeps tone + wording consistent with the React component.
function renderTimeBadge(rawTime) {
  if (!rawTime) return '';
  let primary = '';
  let secondary = '';
  let tone = 'default';
  let icon = '🕐';

  const now = new Date();
  const today0 = new Date(now); today0.setHours(0, 0, 0, 0);

  const fmt12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = parseInt(h, 10);
    const min = (m || '00').padStart(2, '0');
    const h12 = ((hr + 11) % 12) + 1;
    return `${h12}:${min} ${hr >= 12 ? 'PM' : 'AM'}`;
  };
  const dateLabel = (d) => {
    const day = new Date(d); day.setHours(0, 0, 0, 0);
    const diff = (day - today0) / 86400000;
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    const opts = { weekday: 'short', month: 'short', day: 'numeric' };
    return day.toLocaleDateString('en-IE', opts);
  };

  try {
    const p = JSON.parse(rawTime);
    const d = new Date(`${p.date}T00:00:00`);
    const label = dateLabel(d);
    if (p.type === 'flexible') {
      primary = 'Flexible delivery';
      secondary = label;
      tone = label === 'Today' ? 'today' : 'flexible';
      icon = '📅';
    } else if (p.type === 'range' && p.from && p.to) {
      primary = `Deliver ${fmt12(p.from)} – ${fmt12(p.to)}`;
      secondary = label;
      tone = label === 'Today' ? 'today' : 'soon';
      icon = '🕐';
    } else {
      const [hh, mm] = (p.before || '00:00').split(':').map((x) => parseInt(x, 10));
      const when = new Date(d); if (!Number.isNaN(hh)) when.setHours(hh, mm || 0, 0, 0);
      const hoursAway = (when - now) / 3_600_000;
      if (hoursAway <= 2) {
        primary = 'ASAP delivery';
        secondary = `by ${fmt12(p.before)}`;
        tone = 'urgent';
        icon = '⚡';
      } else if (label === 'Today') {
        primary = `Deliver before ${fmt12(p.before)}`;
        secondary = 'Today';
        tone = 'today';
        icon = '🕐';
      } else {
        primary = `Deliver before ${fmt12(p.before)}`;
        secondary = label;
        tone = 'soon';
        icon = '🕐';
      }
    }
  } catch (_) {
    return '';
  }

  const styles = {
    urgent:   'background:#fee2e2;color:#991b1b;border:1px solid #fecaca;',
    today:    'background:#fef3c7;color:#78350f;border:1px solid #fde68a;',
    soon:     'background:#eff6ff;color:#1e3a8a;border:1px solid #bfdbfe;',
    flexible: 'background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0;',
    default:  'background:#f3f4f6;color:#374151;border:1px solid #e5e7eb;',
  };
  return `<div style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;font-size:12px;font-weight:700;${styles[tone]}"><span style="font-size:14px;line-height:1;">${icon}</span><span>${escapeHtml(primary)}${secondary ? `<span style="opacity:0.8;font-weight:500;margin-left:6px;">· ${escapeHtml(secondary)}</span>` : ''}</span></div>`;
}

export default JobsMapView;
